import { isInsideCodeSpan } from "../patterns";
import { withCachedMarkdownDocLines } from "../markdown-doc-cache";

export type MarkdownMathSyntaxKind = "inline" | "block";

export interface MarkdownMathSyntaxMatch {
    fromIndex: number;
    toIndex: number;
    formula: string;
    raw: string;
    kind: MarkdownMathSyntaxKind;
    displayMode: boolean;
}

export interface MarkdownMathBlockRange {
    startLine: number;
    endLine: number;
    fromIndex: number;
    toIndex: number;
    formula: string;
    raw: string;
}

interface MarkdownDocLike {
    lines: number;
    line(number: number): { text: string };
}

function isEscaped(text: string, index: number): boolean {
    let slashCount = 0;
    for (let i = index - 1; i >= 0 && text[i] === "\\"; i -= 1) {
        slashCount += 1;
    }
    return slashCount % 2 === 1;
}

function isSingleDollar(text: string, index: number): boolean {
    return text[index] === "$" &&
        text[index - 1] !== "$" &&
        text[index + 1] !== "$" &&
        !isEscaped(text, index);
}

function canOpenInlineMath(text: string, index: number): boolean {
    if (!isSingleDollar(text, index)) return false;
    if (isInsideCodeSpan(text, index)) return false;
    const next = text[index + 1];
    if (!next || /\s/.test(next)) return false;
    return true;
}

function canCloseInlineMath(text: string, index: number): boolean {
    if (!isSingleDollar(text, index)) return false;
    if (isInsideCodeSpan(text, index)) return false;
    const prev = text[index - 1];
    if (!prev || /\s/.test(prev)) return false;
    return true;
}

function findClosingInlineDollar(text: string, openIndex: number): number {
    for (let i = openIndex + 1; i < text.length; i += 1) {
        if (!canCloseInlineMath(text, i)) continue;
        return i;
    }
    return -1;
}

export function findMarkdownInlineMathSyntaxMatches(
    text: string,
): MarkdownMathSyntaxMatch[] {
    const matches: MarkdownMathSyntaxMatch[] = [];
    for (let i = 0; i < text.length; i += 1) {
        if (!canOpenInlineMath(text, i)) continue;
        const closeIndex = findClosingInlineDollar(text, i);
        if (closeIndex < 0) continue;
        const formula = text.slice(i + 1, closeIndex).trim();
        if (!formula) continue;
        matches.push({
            fromIndex: i,
            toIndex: closeIndex + 1,
            formula,
            raw: text.slice(i, closeIndex + 1),
            kind: "inline",
            displayMode: false,
        });
        i = closeIndex;
    }
    return matches;
}

function openingDisplayMathIndex(text: string): number {
    const leading = text.match(/^\s*/)?.[0].length ?? 0;
    if (leading > 3) return -1;
    return text.startsWith("$$", leading) ? leading : -1;
}

function closingDisplayMathIndex(text: string): number {
    const leading = text.match(/^\s*/)?.[0].length ?? 0;
    if (leading > 3) return -1;
    return text.startsWith("$$", leading) ? leading : -1;
}

export function collectMarkdownMathBlockRanges(
    doc: MarkdownDocLike,
    excludedLines: ReadonlySet<number> = new Set(),
): MarkdownMathBlockRange[] {
    doc = withCachedMarkdownDocLines(doc);
    const ranges: MarkdownMathBlockRange[] = [];
    let lineNumber = 1;
    while (lineNumber <= doc.lines) {
        if (excludedLines.has(lineNumber)) {
            lineNumber += 1;
            continue;
        }
        const line = doc.line(lineNumber);
        const openIndex = openingDisplayMathIndex(line.text);
        if (openIndex < 0) {
            lineNumber += 1;
            continue;
        }

        const singleLineClose = line.text.indexOf("$$", openIndex + 2);
        if (singleLineClose >= 0) {
            const formula = line.text.slice(openIndex + 2, singleLineClose).trim();
            if (formula) {
                ranges.push({
                    startLine: lineNumber,
                    endLine: lineNumber,
                    fromIndex: openIndex,
                    toIndex: singleLineClose + 2,
                    formula,
                    raw: line.text.slice(openIndex, singleLineClose + 2),
                });
            }
            lineNumber += 1;
            continue;
        }

        const formulaLines: string[] = [];
        const openingRemainder = line.text.slice(openIndex + 2);
        if (openingRemainder.trim()) {
            formulaLines.push(openingRemainder);
        }
        let closeLine = -1;
        let closeIndex = -1;
        for (let nextLineNumber = lineNumber + 1; nextLineNumber <= doc.lines; nextLineNumber += 1) {
            if (excludedLines.has(nextLineNumber)) break;
            const nextLine = doc.line(nextLineNumber);
            closeIndex = closingDisplayMathIndex(nextLine.text);
            if (closeIndex >= 0) {
                const closingPrefix = nextLine.text.slice(0, closeIndex);
                if (closingPrefix.trim()) {
                    formulaLines.push(closingPrefix);
                }
                closeLine = nextLineNumber;
                break;
            }
            formulaLines.push(nextLine.text);
        }

        if (closeLine < 0) {
            lineNumber += 1;
            continue;
        }

        const formula = formulaLines.join("\n").trim();
        if (formula) {
            const rawLines: string[] = [];
            for (let rawLineNumber = lineNumber; rawLineNumber <= closeLine; rawLineNumber += 1) {
                const rawLine = doc.line(rawLineNumber).text;
                if (rawLineNumber === lineNumber) {
                    rawLines.push(rawLine.slice(openIndex));
                } else if (rawLineNumber === closeLine) {
                    rawLines.push(rawLine.slice(0, closeIndex + 2));
                } else {
                    rawLines.push(rawLine);
                }
            }
            ranges.push({
                startLine: lineNumber,
                endLine: closeLine,
                fromIndex: openIndex,
                toIndex: closeIndex + 2,
                formula,
                raw: rawLines.join("\n"),
            });
        }
        lineNumber = closeLine + 1;
    }
    return ranges;
}

export function rangeContainsMarkdownMath(
    ranges: readonly MarkdownMathSyntaxMatch[],
    index: number,
): boolean {
    return ranges.some((range) =>
        index >= range.fromIndex && index < range.toIndex,
    );
}

export function rangeContainsMarkdownMathBlock(
    ranges: readonly MarkdownMathBlockRange[],
    lineNumber: number,
): boolean {
    return ranges.some((range) =>
        lineNumber >= range.startLine && lineNumber <= range.endLine,
    );
}
